import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { NETWORK_ERROR_MESSAGE } from "../../lib/apiError";

export const BASE_URL = "https://api.niqsng.org/api";
export const STORAGE_KEY = "logbook_auth_user_v2";

const REQUEST_TIMEOUT_MS = 15_000;   // 15 s for normal requests
const UPLOAD_TIMEOUT_MS  = 300_000;  // 5 min for multipart/file uploads

const rawBaseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  prepareHeaders: (headers) => {
    const token = localStorage.getItem("token");
    if (token) headers.set("Authorization", `Bearer ${token}`);
    headers.set("Accept", "application/json");
    return headers;
  },
});

/**
 * What a member should read when the request never reached the server.
 *
 * fetch reports those as a FETCH_ERROR carrying a raw DOMException string —
 * "AbortError: signal is aborted without reason", "Failed to fetch" — and
 * screens all over the portal fall back to `err.error` when there is no
 * `data.message`, so that string gets rendered verbatim. It means nothing to
 * the person reading it and looks like a crash. Replacing it here, at the one
 * place every request passes through, fixes it everywhere at once.
 */
export { NETWORK_ERROR_MESSAGE };

// Rewrites the message of a no-response error, keeping `status` (screens
// branch on FETCH_ERROR/TIMEOUT_ERROR) and stashing the original cause in
// `originalError` for the few callers that need to know it was an abort.
const withFriendlyNetworkMessage = (result) => {
  const err = result?.error;
  if (!err) return result;
  if (err.status !== "FETCH_ERROR" && err.status !== "TIMEOUT_ERROR") return result;

  return {
    ...result,
    error: {
      ...err,
      error: NETWORK_ERROR_MESSAGE,
      originalError: typeof err.error === "string" ? err.error : String(err.error ?? ""),
    },
  };
};

// Wraps rawBaseQuery with a timeout so isLoading → isError when the server
// is unreachable, while giving file uploads a much longer window.
// AbortErrors that come from RTK Query's own internal abort (not our timeout)
// are returned as a structured FETCH_ERROR so they don't surface as raw
// "signal is aborted without reason" messages in the UI.
const baseQueryWithTimeout = async (args, api, extraOptions) => {
  const body = typeof args === "object" ? args.body : null;
  const isUpload = body instanceof FormData;
  const timeoutMs = isUpload ? UPLOAD_TIMEOUT_MS : REQUEST_TIMEOUT_MS;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  // Merge our AbortSignal into the request args
  const argsWithSignal =
    typeof args === "string"
      ? { url: args, signal: controller.signal }
      : { ...args, signal: controller.signal };

  try {
    const result = await rawBaseQuery(argsWithSignal, api, extraOptions);
    clearTimeout(timeoutId);
    // fetchBaseQuery catches the abort itself and RETURNS a FETCH_ERROR rather
    // than throwing, so the catch below never sees it — this is the path that
    // was leaking "AbortError: signal is aborted without reason" to the screen.
    return withFriendlyNetworkMessage(result);
  } catch (error) {
    clearTimeout(timeoutId);
    if (controller.signal.aborted) {
      // Our own timeout fired
      return {
        error: {
          status: "CUSTOM_ERROR",
          error: NETWORK_ERROR_MESSAGE,
          originalError: "TimeoutError",
        },
      };
    }
    // RTK Query's own internal abort (component unmount, subscription cleanup).
    // Return as a structured error instead of throwing so it doesn't leak into
    // UI catch blocks as a raw DOMException message.
    if (error?.name === "AbortError") {
      return {
        error: {
          status: "FETCH_ERROR",
          error: NETWORK_ERROR_MESSAGE,
          originalError: "AbortError",
        },
      };
    }
    throw error;
  }
};

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: async (args, api, extraOptions) => {
    const result = await baseQueryWithTimeout(args, api, extraOptions);

    if (result.error?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem(STORAGE_KEY);
      window.location.href = "/login";
    }

    return result;
  },
  tagTypes: [
    "Roles",
    "Designations",
    "Departments",
    "LogbookDiets",
    "ExecutiveSets",
    "ExecutiveOffices",
    "Chapters",
    "ChapterDiets",
    "Admins",
    "Assessors",
    "Assessor",
    "AssessorDiets",
    "AssessorLogbooks",
    "LogbookWeek",
    "LogbookWeeksStatus",
    "Logbook",
    "LogbookMessages",
    "ProbationerApplications",
    "ProbationerApplicationDetails",
    "UserCategories",
    "MembershipBands",
    "ChargeTypes",
    "ChargePrices",
    "Dues",
    "LogbookSupervisorRequests",
    "SupervisorRequests",
    "DietUnassignedLogbooks",
    "DietDetails",
    // Logbook Assessment Diets (separate assessment cycle)
    "LbAssessDiets",
    "LbAssessDietDetails",
    "LbAssessAwaiting",
    "LbMyAssessDiets",
    "LbMyAssessDietLogbooks",
    "LbResultApproval",
    // Per-diet wording for the candidate's result letter
    "LbResultLetter",
    "NecOffice",
    // Portal settings — fees, eligibility bars, numbering series
    "PortalSettings",
    // Membership Committee review cycles for grade upgrades
    "UpgradeReviewDiets",
    // Entry-application review assignment, per diet
    "ProbationerReviewQueue",
    // Entry numbers (S/P/T): the series' positions and the approved
    // applications waiting for one. Issuing moves the series on, so every
    // proposed number on screen goes stale at once.
    "ProbationerEntryNumbers",
    // Membership Committee review of entry applications
    "MyEntryReviews",
    "CommitteeEntryApps",

    "ExamDiets",
    "DietExams",
    "ExamCenters",
    "MyExamApps",
    "ExamApp",
    "AdminExams",
    "AdminPapers",
    "AdminExamDiets",
    "AdminExamCenters",
    "AdminSlots",
    "AdminApps",
    "Committees",
    "CommitteeMembers",
    "States",
    "Sector",
    "State",
    "LGA",
    "Chapter",
    "FinanceSummary",
    "Invoices",
    "Payments",
    "Members",
    "MembershipDashboard",
    "Receipts",
    "Receipt",
    "Auth",
    "UpgradeApplications",
    "UpgradeApplicationDetails",
    // Membership Committee review of upgrade applications
    "UpgradeCommitteeReviews",
    "MyUpgradeReviews",
    "CommitteeUpgradeApps",
    "FirmApplications",
    "FirmApplicationDetails",
    // The register of firms that have been issued a number — distinct from the
    // applications above, which is the paperwork that produced them.
    "Firms",
    // A firm as its own owner sees it — their firm, its roster and its dues.
    "MyFirms",
    "FirmPartnerRequests",
    "FirmRefereeRequests",
    "FirmCommitteeReviews",

    // Examiner role
    "ExaminerAssignments",
    "ExaminerResults",
    "ExaminerFeedback",

    // Chief Examiner role
    "ChiefExaminers",
    "ChiefAssignments",
    "ChiefResults",
    "ChiefCourses",

    // Chief Moderator role
    "ChiefModeratorModerators",
    "ChiefModeratorAssignments",
    "ChiefModeratorResults",
    "ChiefModeratorCourses",

    // Moderator role
    "ModeratorAssignments",
    "ModeratorResults",

    // Chairman of Moderation Board
    "ChairmanResults",

    // Admin: exam courses
    "AdminExamCourses",

    // Exam Calls (new controller)
    "ExamCalls",
    "MyExamCallApps",
    "AdminExamCalls",

    // Events & CPD
    "Events",
    "AdminEvents",
    "EventRegistrations",
    "MyEventRegistrations",
    "EventMaterials",
    "EventRefreshments",
    // Files an event hands out (programme, papers, decks) — not EventMaterials,
    // which is the physical stock issued at the desk.
    "EventDocuments",
    "CpdLedger",
    "AdminCpd",
    "CpdSummary",
    "AccreditedInstitutes",
    // A chapter's own CPD event, from the chairman's request through to the
    // allocation that creates the event. One tag for both ends of the flow, so
    // the secretariat's approval refreshes the chairman's screen.
    "ChapterEventRequests",

    // Graduate Pledge — final-year students' declaration of intent. The
    // students are not members and have no portal account; these tags cover
    // only the secretariat's side of it (the student's form is fetched with a
    // plain `fetch`, since it must work with no session at all).
    "GraduatePledgeCohorts",
    "GraduatePledgeSubmissions",

    // The QSRBN register of institutions teaching QS. Distinct from
    // AccreditedInstitutes above, which is the events module's list of bodies
    // that host CPD — a body can be in that one without teaching anybody.
    "QsInstitutions",

    // A Head of Department's own pledge approval queue.
    "HodPledgeApplications",

    // Secretariat mail-out
    "MailCampaigns",
    "MailCampaign",
    "MailCampaignRecipients",
    "MailGroups",

    // Support & Ticketing
    "Tickets",
    "MyTickets",
    "AdminTickets",
    "TicketCategories",
    "TicketReplies",
    "TicketHistory",
    "LedgerAccounts",
    "ReminderLogs",
    "PaymentEditRequests",

    // Databank (material/item prices per area per time)
    "DatabankItems",
    "DatabankFilters",
    "DatabankCategories",
    // Per-type registers (composite rates, unit rates, …). Tagged separately
    // from the original price list so a write to one register does not make
    // every other register refetch.
    "DatasetItems",
    "DatasetFilters",
    "DatasetCategories",
    // Elemental cost analyses — a document library, not a figure register.
    "ElementalCostDocuments",
    "ElementalCostFilters",

    // HR (secretariat staff, attendance, leave)
    "HrStaff",
    "HrStaffDetails",
    "HrAttendance",
    "HrAttendanceSummary",
    "HrLeaveTypes",
    "HrLeaveRequests",
    "HrLeaveBalances",

    // Stores (item register, the stores themselves, stock movements, and what
    // admins are holding once stock has been issued to them)
    "StoreItems",
    "StoreItemDetails",
    "StoreMovements",
    "StoreSummary",
    "Stores",
    "StoreHoldings",
    "StoreReturns",
    "StoreRequests",

    // National positions (past presidents, trustees, national bodies, reps)
    "NationalPositions",

    // The National Policy Committee's closed forum. "ForumAccess" is the
    // yes/no that decides whether the sidebar shows the module at all, and
    // is kept apart from the content tags so opening a thread does not make
    // the whole portal re-ask whether you are allowed in.
    "ForumAccess",
    "ForumThreads",
    "ForumThread",
    "ForumMembers",
    // The category sidebar's counts. Its own tag because a new discussion
    // moves one of them, and nothing else on the screen should have to be
    // re-fetched to find that out.
    "ForumCategories",
  ],

  endpoints: () => ({}),
});
